import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Satellite, Megaphone, Save, AlertTriangle, Ban } from 'lucide-react';
import {
  dashboardApi,
  DASHBOARD_KEY,
  SCHAKELBORD_KEY,
  BLOKKADES_KEY,
  KANAAL_CATEGORIEEN,
  type KanaalCategorie,
  type KanaalKeuze,
  type RadarInstellingen,
} from '../../services/professionalDashboardApi';
import {
  PROFESSIONAL_TYPE_LABELS,
  type ProfessionalType,
} from '../../types/professionalRates';

const KEUZES: KanaalKeuze[] = ['email', 'sms', 'beide', 'geen'];

/** Eigen blokkades van opdrachtgevers (migratie 188); opheffen werkt direct, los van "Opslaan". */
const GeblokkeerdeOpdrachtgevers: React.FC = () => {
  const { t, i18n } = useTranslation('professionalDashboard');
  const queryClient = useQueryClient();
  const { data: lijst = [] } = useQuery({ queryKey: BLOKKADES_KEY, queryFn: dashboardApi.blokkades });
  const opheffen = useMutation({
    mutationFn: (employerId: string) => dashboardApi.deblokkeer(employerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BLOKKADES_KEY });
      queryClient.invalidateQueries({ queryKey: ['professional-werkplek-opdrachten'] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
      toast.success(t('blacklist.opgeheven'));
    },
    onError: () => toast.error(t('schakelbord.fout')),
  });
  const datum = new Intl.DateTimeFormat(i18n.language?.startsWith('en') ? 'en-GB' : 'nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="border-t border-gray-100 px-5 py-4">
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-navy-500">
        <Ban className="w-4 h-4" />
        {t('blacklist.blokTitel')}
      </h3>
      <p className="mt-1 text-xs text-navy-500">{t('blacklist.blokUitleg')}</p>
      {lijst.length === 0 ? (
        <p className="mt-2 text-xs text-navy-400">{t('blacklist.blokLeeg')}</p>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100">
          {lijst.map((b) => (
            <li key={b.wederpartij_id} className="flex items-center justify-between gap-3 py-2">
              <span className="text-sm text-navy-800">
                {b.naam} <span className="text-xs text-navy-400">· {t('blacklist.blokSinds', { datum: datum.format(new Date(b.sinds)) })}</span>
              </span>
              <button
                type="button"
                onClick={() => opheffen.mutate(b.wederpartij_id)}
                disabled={opheffen.isPending}
                className="rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-semibold text-navy-700 hover:border-navy-300"
              >
                {t('blacklist.opheffen')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
type RadarConcept = Pick<
  RadarInstellingen,
  'actief' | 'disciplines' | 'volgt_profiel' | 'radius_km'
>;
/** Waarde van de radius-dropdown: profiel, 15/30/50 of landelijk. */
type RadiusWaarde = 'profiel' | '15' | '30' | '50' | 'landelijk';

/**
 * Onderdeel 2: Job-Radar en communicatiekanalen, met één opslagknop.
 * Radar-disciplines leeg = alle eigen disciplines; de server laat alleen
 * disciplines uit de eigen screening toe.
 */
export const Schakelbord: React.FC = () => {
  const { t } = useTranslation('professionalDashboard');
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: SCHAKELBORD_KEY,
    queryFn: dashboardApi.schakelbord,
  });
  const [radar, setRadar] = useState<RadarConcept | null>(null);
  const [kanalen, setKanalen] = useState<Record<
    KanaalCategorie,
    KanaalKeuze
  > | null>(null);

  useEffect(() => {
    if (!data) return;
    const eigen = data.eigen_disciplines;
    // Leeg opgeslagen = alles aan: toon dat ook zo in de checkboxes.
    setRadar({
      ...data.radar,
      disciplines: data.radar.disciplines.length
        ? data.radar.disciplines
        : eigen,
    });
    setKanalen(data.kanalen);
  }, [data]);

  const opslaan = useMutation({
    mutationFn: () => {
      // Server: lege lijst = alle eigen disciplines. Alles aangevinkt → leeg (ook toekomstige disciplines);
      // niets aangevinkt → radar uit.
      const r = radar!;
      const alles = data!.eigen_disciplines.every(d =>
        r.disciplines.includes(d)
      );
      const verzonden =
        r.disciplines.length === 0
          ? { ...r, actief: false }
          : { ...r, disciplines: alles ? [] : r.disciplines };
      return dashboardApi.zetSchakelbord({
        radar: verzonden,
        kanalen: kanalen!,
      });
    },
    onSuccess: nieuw => {
      queryClient.setQueryData(SCHAKELBORD_KEY, nieuw);
      queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
      toast.success(t('schakelbord.opgeslagen'));
    },
    onError: () => toast.error(t('schakelbord.fout')),
  });

  const radiusWaarde: RadiusWaarde = !radar
    ? 'profiel'
    : radar.volgt_profiel
      ? 'profiel'
      : radar.radius_km
        ? (String(radar.radius_km) as RadiusWaarde)
        : 'landelijk';
  const zetRadius = (w: RadiusWaarde) =>
    setRadar(
      r =>
        r && {
          ...r,
          volgt_profiel: w === 'profiel',
          radius_km:
            w === 'profiel' || w === 'landelijk'
              ? null
              : (Number(w) as 15 | 30 | 50),
        }
    );
  const profielRadius = data?.radar.profiel_radius_km
    ? t('schakelbord.km', { km: data.radar.profiel_radius_km })
    : t('schakelbord.landelijk');
  const straalGekozen =
    radiusWaarde === 'profiel'
      ? !!data?.radar.profiel_radius_km
      : radiusWaarde !== 'landelijk';

  return (
    <section
      aria-labelledby="schakelbord-titel"
      className="rounded-2xl border border-gray-200 bg-white shadow-card"
    >
      <h2
        id="schakelbord-titel"
        className="px-5 pt-5 text-base font-semibold text-navy-900"
      >
        {t('schakelbord.titel')}
      </h2>

      {isLoading || !radar || !kanalen ? (
        <div className="p-4 space-y-2">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-8 rounded bg-gray-50 animate-pulse" />
          ))}
        </div>
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault();
            opslaan.mutate();
          }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* 2.1 Job-Radar */}
            <section className="px-5 py-4 border-b border-gray-100 lg:border-b-0 lg:border-r">
              <div className="flex items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-navy-500">
                  <Satellite className="w-4 h-4" />
                  {t('schakelbord.radarTitel')}
                </h3>
                <label className="inline-flex items-center gap-2 text-xs font-medium text-navy-700">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={radar.actief}
                    onChange={e =>
                      setRadar({ ...radar, actief: e.target.checked })
                    }
                    className="rounded border-gray-300 text-navy-900 focus:ring-navy-500"
                  />
                  {t('schakelbord.radarActief')}
                </label>
              </div>
              <p className="mt-1 text-xs leading-relaxed text-navy-500">
                {t('schakelbord.radarUitleg')}
              </p>

              <fieldset className="mt-3" disabled={!radar.actief}>
                <legend className="text-xs font-semibold text-navy-700">
                  {t('schakelbord.disciplines')}
                </legend>
                {data!.eigen_disciplines.length === 0 ? (
                  <p className="mt-1 text-xs text-navy-400">
                    {t('schakelbord.geenDisciplines')}
                  </p>
                ) : (
                  <div className="mt-1 space-y-1">
                    {data!.eigen_disciplines.map(d => (
                      <label
                        key={d}
                        className="flex items-center gap-2 text-sm text-navy-800"
                      >
                        <input
                          type="checkbox"
                          checked={radar.disciplines.includes(d)}
                          onChange={e =>
                            setRadar({
                              ...radar,
                              disciplines: e.target.checked
                                ? [...radar.disciplines, d]
                                : radar.disciplines.filter(x => x !== d),
                            })
                          }
                          className="rounded border-gray-300 text-navy-900 focus:ring-navy-500"
                        />
                        {PROFESSIONAL_TYPE_LABELS[d as ProfessionalType] ?? d}
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>

              <label className="mt-3 block">
                <span className="text-xs font-semibold text-navy-700">
                  {t('schakelbord.radius')}
                </span>
                <select
                  value={radiusWaarde}
                  disabled={!radar.actief}
                  onChange={e => zetRadius(e.target.value as RadiusWaarde)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-navy-500 focus:ring-1 focus:ring-navy-500"
                >
                  <option value="profiel">
                    {t('schakelbord.profielRadius', { radius: profielRadius })}
                  </option>
                  {data!.radius_opties.map(km => (
                    <option key={km} value={String(km)}>
                      {t('schakelbord.km', { km })}
                    </option>
                  ))}
                  <option value="landelijk">
                    {t('schakelbord.landelijk')}
                  </option>
                </select>
              </label>
              {radar.actief && straalGekozen && !data!.locatie_bekend && (
                <p className="mt-2 flex gap-1.5 text-xs text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  {t('schakelbord.locatieOnbekend')}
                </p>
              )}
            </section>

            {/* 2.2 Kanalen */}
            <section className="px-5 py-4">
              <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-navy-500">
                <Megaphone className="w-4 h-4" />
                {t('schakelbord.kanalenTitel')}
              </h3>
              <div className="mt-2 space-y-2.5">
                {KANAAL_CATEGORIEEN.map(c => (
                  <fieldset key={c}>
                    <legend className="text-xs font-medium text-navy-800">
                      {t(`schakelbord.kanaal.${c}`)}
                    </legend>
                    <div className="mt-1 grid grid-cols-4 rounded-lg border border-gray-200 bg-canvas p-0.5">
                      {KEUZES.map(k => (
                        <label
                          key={k}
                          className={`cursor-pointer rounded-md px-1 py-1 text-center text-[11px] font-semibold transition-colors ${
                            kanalen[c] === k
                              ? k === 'geen'
                                ? 'bg-white text-navy-500 shadow-sm'
                                : 'bg-navy-900 text-white'
                              : 'text-navy-500 hover:text-navy-900'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`kanaal-${c}`}
                            value={k}
                            checked={kanalen[c] === k}
                            onChange={() => setKanalen({ ...kanalen, [c]: k })}
                            className="sr-only"
                          />
                          {t(`schakelbord.keuze.${k}`)}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
            </section>
          </div>
          <div className="flex justify-end border-t border-gray-100 px-5 py-4">
            <button
              type="submit"
              disabled={opslaan.isPending}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-navy-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {t('schakelbord.opslaan')}
            </button>
          </div>
        </form>
      )}
      <GeblokkeerdeOpdrachtgevers />
    </section>
  );
};
